<template>
  <v-dialog v-model="dialog" transition="dialog-bottom-transition" scrollable max-width="720">
    <v-card>
      <v-toolbar color="primary" :title="toolbarTitle">
        <v-btn color="black" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>
      <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>
      <v-card-text v-if="sale">
        <v-row dense class="mb-2">
          <v-col cols="12" sm="6" md="3">
            <div class="text-caption text-medium-emphasis">Status</div>
            <v-chip size="small" variant="tonal" :color="statusColor(sale.status)" class="mt-1">
              {{ statusLabel(sale.status) }}
            </v-chip>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="text-caption text-medium-emphasis">Canal</div>
            <div>{{ channelLabel(sale.channel) }}</div>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="text-caption text-medium-emphasis">Identidade</div>
            <div>{{ identityLabel(sale.identity_mode) }}</div>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="text-caption text-medium-emphasis">Meio de pagamento</div>
            <div>{{ paymentRailLabel(sale.payment_rail) }}</div>
          </v-col>
          <v-col
            v-if="sale.identity_mode === 'identified' || sale.beneficiary"
            cols="12"
            sm="6"
            md="3"
          >
            <div class="text-caption text-medium-emphasis">Beneficiário</div>
            <div>{{ personLabel(sale.beneficiary, sale.beneficiary_legal_person_id) }}</div>
          </v-col>
          <v-col
            v-if="sale.payer || sale.payer_legal_person_id"
            cols="12"
            sm="6"
            md="3"
          >
            <div class="text-caption text-medium-emphasis">Pagador</div>
            <div>{{ personLabel(sale.payer, sale.payer_legal_person_id) }}</div>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="text-caption text-medium-emphasis">Total</div>
            <div class="text-subtitle-1 font-weight-medium">{{ formatPrice(sale.amount_total) }}</div>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="text-caption text-medium-emphasis">Pago em</div>
            <div>{{ formatDateTime(sale.paid_at) }}</div>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="text-caption text-medium-emphasis">Fatura</div>
            <div>{{ sale.invoice_id || sale.invoice?.id || '—' }}</div>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <div class="text-caption text-medium-emphasis">Loja</div>
            <div>{{ sale.store?.name || (sale.store_id ? `#${sale.store_id}` : '—') }}</div>
          </v-col>
          <v-col v-if="sale.receipt_sent" cols="12" sm="6" md="3">
            <div class="text-caption text-medium-emphasis">Comprovante / ingressos</div>
            <div>Já enviado</div>
          </v-col>
        </v-row>

        <div class="text-subtitle-2 mb-2">Itens</div>
        <v-data-table
          :headers="itemHeaders"
          :items="saleItems"
          :items-per-page="-1"
          hide-default-footer
          density="compact"
          class="elevation-0"
        >
          <template v-slot:item.product_name="{ item }">
            <span>{{ item.product_name || item.product?.name || '—' }}</span>
            <span v-if="item.variant_label" class="text-medium-emphasis">
              · {{ item.variant_label }}
            </span>
          </template>
          <template v-slot:item.quantity="{ value }">
            <span>{{ formatQty(value) }}</span>
          </template>
          <template v-slot:item.unit_price="{ value }">
            <span>{{ formatPrice(value) }}</span>
          </template>
          <template v-slot:item.subtotal="{ value }">
            <span>{{ formatPrice(value) }}</span>
          </template>
          <template v-slot:no-data>
            <div class="text-center text-medium-emphasis py-4">Nenhum item</div>
          </template>
        </v-data-table>

        <template v-if="saleTickets.length">
          <div class="d-flex align-center justify-space-between mt-4 mb-2 flex-wrap ga-2">
            <div class="text-subtitle-2 mb-0">
              Ingressos ({{ saleTickets.length }})
            </div>
            <v-btn
              v-if="canResendTickets"
              size="small"
              color="primary"
              variant="tonal"
              :disabled="loading || resending"
              @click="openResend"
            >
              <v-icon class="mr-1" size="small">mdi-email-fast-outline</v-icon>
              Reenviar ingressos
            </v-btn>
          </div>
          <v-data-table
            :headers="ticketHeaders"
            :items="saleTickets"
            :items-per-page="-1"
            hide-default-footer
            density="compact"
            class="elevation-0"
          >
            <template v-slot:item.code="{ value }">
              <code class="text-body-2">{{ value }}</code>
              <v-btn
                icon
                variant="text"
                density="compact"
                size="small"
                aria-label="Copiar código"
                @click="copyCode(value)"
              >
                <v-icon size="small">mdi-content-copy</v-icon>
              </v-btn>
            </template>
            <template v-slot:item.status="{ value }">
              <v-chip size="small" variant="tonal" :color="ticketStatusColor(value)">
                {{ ticketStatusLabel(value) }}
              </v-chip>
            </template>
            <template v-slot:item.scans="{ item }">
              <span>{{ item.scan_count ?? 0 }}/{{ item.max_scans ?? 1 }}</span>
            </template>
            <template v-slot:item.used_at="{ value }">
              <span>{{ formatDateTime(value) }}</span>
            </template>
          </v-data-table>
        </template>

        <v-alert
          v-if="refundBlockedByTickets"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-4 mb-0"
        >
          Não é possível estornar: um ou mais ingressos já foram utilizados no check-in.
        </v-alert>
        <v-alert
          v-else-if="canRefund"
          type="warning"
          variant="tonal"
          density="compact"
          class="mt-4 mb-0"
        >
          Estorno devolve o estoque ao pool do grupo e à alocação da loja, anula ingressos
          não usados e marca a fatura como estornada. Só vendas pagas.
        </v-alert>
      </v-card-text>
      <v-card-text v-else-if="!loading" class="text-medium-emphasis">
        Venda não encontrada
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn
          v-if="canResendTickets"
          color="primary"
          variant="tonal"
          :disabled="loading || resending"
          @click="openResend"
        >
          <v-icon class="mr-1">mdi-email-fast-outline</v-icon>
          Reenviar ingressos
        </v-btn>
        <v-btn
          v-if="canRefund"
          color="error"
          variant="tonal"
          :loading="refunding"
          :disabled="refunding || loading || resending"
          @click="confirmOpen = true"
        >
          <v-icon class="mr-1">mdi-cash-refund</v-icon>
          Estornar
        </v-btn>
        <v-btn variant="text" @click="dialog = false">Fechar</v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="confirmOpen" max-width="420" persistent>
      <v-card>
        <v-card-title class="text-h6">Confirmar estorno</v-card-title>
        <v-card-text>
          Estornar a venda #{{ sale?.id }} no valor de
          <strong>{{ formatPrice(sale?.amount_total) }}</strong>? Esta ação devolve o estoque,
          anula ingressos não usados e não pode ser desfeita pela tela.
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" :disabled="refunding" @click="confirmOpen = false">Cancelar</v-btn>
          <v-btn color="error" :loading="refunding" :disabled="refunding" @click="doRefund">
            Confirmar estorno
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="resendOpen" max-width="480" persistent>
      <v-card>
        <v-card-title class="text-h6">Reenviar ingressos</v-card-title>
        <v-card-text>
          <p class="text-body-2 text-medium-emphasis mb-3">
            Reenvia o comprovante da venda #{{ sale?.id }} com os códigos de ingresso
            (não anulados). Só vendas pagas.
          </p>
          <v-btn-toggle
            v-model="resendChannel"
            mandatory
            density="comfortable"
            color="primary"
            class="mb-4"
            divided
          >
            <v-btn value="email" size="small">
              <v-icon start size="small">mdi-email-outline</v-icon>
              E-mail
            </v-btn>
            <v-btn value="whatsapp" size="small">
              <v-icon start size="small">mdi-whatsapp</v-icon>
              WhatsApp
            </v-btn>
          </v-btn-toggle>

          <v-text-field
            v-if="resendChannel === 'email'"
            v-model="resendEmail"
            label="E-mail"
            type="email"
            autocomplete="email"
            density="comfortable"
            variant="outlined"
            hide-details="auto"
            :disabled="resending"
            hint="Opcional: em branco usa o e-mail do pagador/beneficiário no cadastro"
            persistent-hint
          />
          <v-text-field
            v-else
            v-model="resendPhone"
            label="Telefone (WhatsApp)"
            density="comfortable"
            variant="outlined"
            hide-details="auto"
            :disabled="resending"
            placeholder="11999998888"
            hint="Com DDD; o país +55 é aplicado se faltar"
            persistent-hint
          />
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" :disabled="resending" @click="resendOpen = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            :loading="resending"
            :disabled="resending || !canSubmitResend"
            @click="doResend"
          >
            Enviar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>
<script>
import api from '@/services'

const STATUS_LABELS = {
  open: 'Aberta',
  awaiting_payment: 'Aguardando pagamento',
  paid: 'Paga',
  canceled: 'Cancelada',
  expired: 'Expirada',
  refunded: 'Estornada',
}

const STATUS_COLORS = {
  open: 'info',
  awaiting_payment: 'warning',
  paid: 'success',
  canceled: 'error',
  expired: 'default',
  refunded: 'secondary',
}

const CHANNEL_LABELS = {
  pdv: 'PDV',
  web: 'Web',
  admin: 'Admin',
}

const IDENTITY_LABELS = {
  anonymous: 'Anônima',
  identified: 'Identificada',
}

const TICKET_STATUS_LABELS = {
  valid: 'Válido',
  used: 'Utilizado',
  voided: 'Anulado',
}

const TICKET_STATUS_COLORS = {
  valid: 'success',
  used: 'warning',
  voided: 'secondary',
}

export default {
  props: {
    modelValue: Object,
    /** When false, hide refund action (e.g. platform read-only without group). */
    canRefundSales: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:modelValue', 'refunded', 'receipt-sent'],
  data() {
    return {
      dialog: false,
      loading: false,
      refunding: false,
      resending: false,
      confirmOpen: false,
      resendOpen: false,
      resendChannel: 'email',
      resendEmail: '',
      resendPhone: '',
      sale: null,
      itemHeaders: [
        { title: 'Produto', align: 'start', key: 'product_name', sortable: false },
        { title: 'Qtd', align: 'end', key: 'quantity', sortable: false },
        { title: 'Unitário', align: 'end', key: 'unit_price', sortable: false },
        { title: 'Subtotal', align: 'end', key: 'subtotal', sortable: false },
      ],
      ticketHeaders: [
        { title: 'Código', align: 'start', key: 'code', sortable: false },
        { title: 'Produto', align: 'start', key: 'product_name', sortable: false },
        { title: 'Status', align: 'start', key: 'status', sortable: false },
        { title: 'Scans', align: 'end', key: 'scans', sortable: false },
        { title: 'Usado em', align: 'end', key: 'used_at', sortable: false },
      ],
    }
  },
  computed: {
    toolbarTitle() {
      if (this.sale?.id) return `Venda #${this.sale.id}`
      if (this.modelValue?.id) return `Venda #${this.modelValue.id}`
      return 'Detalhe da venda'
    },
    saleItems() {
      return Array.isArray(this.sale?.items) ? this.sale.items : []
    },
    saleTickets() {
      return Array.isArray(this.sale?.tickets) ? this.sale.tickets : []
    },
    activeTickets() {
      return this.saleTickets.filter((t) => t.status !== 'voided')
    },
    refundBlockedByTickets() {
      if (this.sale?.status !== 'paid') return false
      return this.saleTickets.some(
        (t) => t.status === 'used' || Number(t.scan_count || 0) > 0
      )
    },
    canRefund() {
      return (
        this.canRefundSales &&
        this.sale?.status === 'paid' &&
        this.sale?.id != null &&
        !this.refundBlockedByTickets
      )
    },
    /** Paid sale with at least one non-voided issued ticket. */
    canResendTickets() {
      return (
        this.sale?.status === 'paid' &&
        this.sale?.id != null &&
        this.activeTickets.length > 0
      )
    },
    canSubmitResend() {
      if (this.resendChannel === 'whatsapp') {
        return String(this.resendPhone || '').replace(/\D/g, '').length >= 8
      }
      // Email optional — BE resolves from member; if filled, basic shape check
      const e = String(this.resendEmail || '').trim()
      if (!e) return true
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)
    },
  },
  methods: {
    statusLabel(value) {
      return STATUS_LABELS[value] || value || '—'
    },
    statusColor(value) {
      return STATUS_COLORS[value] || 'default'
    },
    channelLabel(value) {
      return CHANNEL_LABELS[value] || value || '—'
    },
    identityLabel(value) {
      return IDENTITY_LABELS[value] || value || '—'
    },
    paymentRailLabel(value) {
      if (!value) return '—'
      const map = {
        pix: 'Pix',
        tap: 'TAP',
        invoice: 'Cobrança (fatura)',
        cash: 'Dinheiro',
      }
      return map[value] || String(value)
    },
    personLabel(person, id) {
      if (person?.name) {
        return person.ssn ? `${person.name} (${person.ssn})` : person.name
      }
      if (id != null) return `#${id}`
      return '—'
    },
    ticketStatusLabel(value) {
      return TICKET_STATUS_LABELS[value] || value || '—'
    },
    ticketStatusColor(value) {
      return TICKET_STATUS_COLORS[value] || 'default'
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      if (isNaN(n)) return value
      return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    formatQty(value) {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      if (isNaN(n)) return value
      return n.toLocaleString('pt-BR')
    },
    formatDateTime(value) {
      if (!value) return '—'
      if (this.$moment) {
        return this.$moment(value).format('DD/MM/YYYY HH:mm')
      }
      try {
        return new Date(value).toLocaleString('pt-BR')
      } catch {
        return value
      }
    },
    copyCode(code) {
      if (!code || !navigator?.clipboard?.writeText) {
        this.$notify({ type: 'warn', text: 'Não foi possível copiar' })
        return
      }
      navigator.clipboard
        .writeText(String(code))
        .then(() => {
          this.$notify({ type: 'success', text: 'Código copiado' })
        })
        .catch(() => {
          this.$notify({ type: 'warn', text: 'Não foi possível copiar' })
        })
    },
    loadSale() {
      const vm = this
      const id = vm.modelValue?.id
      if (!id) {
        vm.sale = null
        return Promise.resolve()
      }
      vm.loading = true
      return api.sale
        .get(id)
        .then((result) => {
          vm.sale = result
        })
        .catch((err) => {
          vm.sale = null
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          vm.loading = false
        })
    },
    doRefund() {
      const vm = this
      const id = vm.sale?.id
      if (!id || vm.refunding) return
      vm.refunding = true
      return api.sale
        .refund(id)
        .then((result) => {
          vm.sale = result || { ...vm.sale, status: 'refunded' }
          vm.confirmOpen = false
          vm.$notify({ type: 'success', text: `Venda #${id} estornada` })
          vm.$emit('refunded', result || vm.sale)
        })
        .catch((err) => {
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          vm.refunding = false
        })
    },
    openResend() {
      if (!this.canResendTickets) return
      this.resendChannel = 'email'
      this.resendEmail = ''
      this.resendPhone = this.sale?.payer_phone
        ? String(this.sale.payer_phone).replace(/^\+55/, '').replace(/\D/g, '')
        : ''
      this.resendOpen = true
    },
    doResend() {
      const vm = this
      const id = vm.sale?.id
      if (!id || vm.resending || !vm.canSubmitResend) return
      vm.resending = true

      const promise =
        vm.resendChannel === 'whatsapp'
          ? api.sale.receiptWhatsApp(id, { phone: String(vm.resendPhone).trim() })
          : api.sale.receiptEmail(id, {
              email: String(vm.resendEmail || '').trim() || undefined,
            })

      return promise
        .then((result) => {
          const to = result?.to || ''
          const channel = result?.channel || vm.resendChannel
          if (result?.sale) {
            vm.sale = { ...vm.sale, ...result.sale, receipt_sent: true }
          } else {
            vm.sale = { ...vm.sale, receipt_sent: true }
          }
          vm.resendOpen = false
          const label = channel === 'whatsapp' ? 'WhatsApp' : 'e-mail'
          vm.$notify({
            type: 'success',
            text: to
              ? `Ingressos enfileirados por ${label} para ${to}`
              : `Ingressos enfileirados por ${label}`,
          })
          vm.$emit('receipt-sent', result || { sale: vm.sale, channel })
        })
        .catch((err) => {
          if (err?.message) {
            vm.$notify({ type: 'warn', text: err.message })
          }
        })
        .finally(() => {
          vm.resending = false
        })
    },
  },
  watch: {
    modelValue(newVal) {
      if (newVal?.id && !this.dialog) {
        this.dialog = true
      }
    },
    dialog(newVal) {
      if (newVal) {
        this.confirmOpen = false
        this.resendOpen = false
        this.loadSale()
      } else {
        this.sale = null
        this.loading = false
        this.refunding = false
        this.resending = false
        this.confirmOpen = false
        this.resendOpen = false
        this.resendEmail = ''
        this.resendPhone = ''
        this.$emit('update:modelValue', {})
      }
    },
  },
}
</script>
